import React from "react";
import { MdBlock, MdCheckCircle, MdLink, MdShield } from "react-icons/md";
import {
  ADMIN_GRANTS,
  GRANTS,
  RESTRICTIONS,
  ROLE_LABELS,
  isOauthAdminRole,
} from "../../../../constants/oauth";
import { BtnLoader, CardHeader } from "../OAuthShell/OAuthShell";
import { useTranslation } from "react-i18next";

// Scenario 1 -- the consent screen for a signed-in eligible account.
// Purely presentational: no API calls, no storage access.
const ConsentCard = ({
  clientName,
  user,
  role,
  scopeItems,
  redirectHost,
  submitting,
  apiError,
  onApprove,
  onDeny,
  onSwitchAccount,
  onRetry,
}) => {
  const { t } = useTranslation(["auth", "common"]);
  // An Admin token is not scoped to one account, so it must not be shown the
  // shipper copy, every line of which is phrased as "your". The role here is
  // the one the BACKEND returned, read from the session Authorize.jsx already
  // checked against OAUTH_ELIGIBLE_ROLES -- an admin only ever reaches this
  // card with a session it brought from the admin portal.
  const grants = isOauthAdminRole(role) ? ADMIN_GRANTS : GRANTS;
  const grantsKey = isOauthAdminRole(role) ? "adminGrants" : "grants";
  const displayName =
    [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
    user?.username ||
    user?.email ||
    t("oauth.yourAccount");
  // Initials rather than profile_image: that field can be the literal string
  // "undefined" in this codebase.
  const initials = (displayName.match(/\b\w/g) || [])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <CardHeader
        icon={<MdLink size={22} aria-hidden="true" />}
        title={t("oauth.connectTitle", { client: clientName })}
        subtitle={t("oauth.connectSubtitle")}
      />

      {/* Identity row -- answers "which account am I in?" without leaving. */}
      <div className="flex items-center gap-2.5 border-b border-ui-border bg-ui-surface-sunken px-5 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-light text-xs font-bold text-brand">
          {initials || "?"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-[13px] font-semibold text-ui-text">
              {displayName}
            </p>
            <span className="shrink-0 rounded-full bg-state-info-bg px-2 py-0.5 text-[10px] font-semibold text-state-info-text">
              {t(`oauth.roles.${role}`, { defaultValue: ROLE_LABELS[role] || role })}
            </span>
          </div>
          {user?.email && (
            <p className="truncate text-[11px] text-ui-text-muted">
              {user.email}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onSwitchAccount}
          disabled={submitting}
          className="shrink-0 text-[11px] font-semibold text-brand hover:underline disabled:opacity-50"
        >
          {t("oauth.notYou")}
        </button>
      </div>

      <div className="px-5 pt-4">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.4px] text-ui-text-muted">
          {t("oauth.willBeAble", { client: clientName })}
        </p>
        <ul className="space-y-2.5">
          {grants.map((grant) => (
            <li key={grant.key} className="flex gap-2.5">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-state-success-bg">
                <MdCheckCircle
                  size={12}
                  className="text-state-success-text"
                  aria-hidden="true"
                />
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-medium text-ui-text">
                  {t(`oauth.${grantsKey}.${grant.key}.title`, { defaultValue: grant.title })}
                </span>
                <span className="block text-[11.5px] text-ui-text-muted">
                  {t(`oauth.${grantsKey}.${grant.key}.detail`, { defaultValue: grant.detail })}
                </span>
              </span>
            </li>
          ))}
        </ul>

        {/* The restrictions block is the main trust lever -- do not drop it. */}
        <p className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-[0.4px] text-ui-text-muted">
          {t("oauth.willNotBeAble", { client: clientName })}
        </p>
        <ul className="space-y-2.5">
          {RESTRICTIONS.map((item) => (
            <li key={item.key} className="flex gap-2.5">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-state-danger-bg">
                <MdBlock
                  size={12}
                  className="text-state-danger"
                  aria-hidden="true"
                />
              </span>
              <span className="text-[13px] text-ui-text-muted">
                {t(`oauth.restrictions.${item.key}`, { defaultValue: item.title })}
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex gap-2 rounded-md bg-state-success-bg px-3 py-2 text-xs text-state-success-text">
          <MdShield size={16} className="shrink-0" aria-hidden="true" />
          <span>
            {t("oauth.readOnly", { client: clientName })}
          </span>
        </div>

        {/* Host only, never the raw redirect_uri, state or code_challenge. */}
        <details className="mt-4">
          <summary className="cursor-pointer list-none text-[11px] font-semibold text-ui-text-muted hover:text-ui-text">
            {t("oauth.details")}
          </summary>
          <div className="mt-2 space-y-1 rounded-lg border border-ui-border bg-ui-surface-sunken p-3 text-[11px] text-ui-text-muted">
            <p>
              {t("oauth.application", { client: clientName })}
              {redirectHost ? ` (${redirectHost})` : ""}
            </p>
            <p>
              {t("oauth.scopesLine", {
                scopes: (scopeItems || [])
                  .map((s) =>
                    t(`oauth.scopes.${s.key}`, { count: s.count, defaultValue: s.label })
                  )
                  .join(" · "),
              })}
            </p>
            <p>{t("oauth.security")}</p>
          </div>
        </details>

        {apiError && (
          <div
            role="alert"
            className="mt-4 rounded-md bg-state-danger-bg px-3 py-2 text-xs text-state-danger"
          >
            {apiError}
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-1 block font-semibold underline"
              >
                {t("common:actions.tryAgain")}
              </button>
            )}
          </div>
        )}
      </div>

      <div
        className="mt-4 flex flex-col-reverse gap-2 border-t border-ui-border px-5 py-4 sm:flex-row"
        aria-busy={submitting}
      >
        <button
          type="button"
          onClick={onDeny}
          disabled={submitting}
          className="h-11 flex-1 rounded-md border border-ui-border bg-ui-surface text-sm font-semibold text-ui-text transition-colors hover:bg-ui-surface-sunken disabled:opacity-50"
        >
          {t("oauth.deny")}
        </button>
        {/* Deliberately a button, not a form submit, and deliberately NOT
            autofocused: approving a grant must be a click. An autofocused
            Authorize button turns a stray Enter or Space -- left over from
            typing a password on the previous screen -- into consent. */}
        <button
          type="button"
          onClick={onApprove}
          disabled={submitting}
          className="h-11 flex-1 rounded-md bg-brand text-sm font-semibold text-white transition-colors hover:bg-brand-hover active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? <BtnLoader /> : t("oauth.authorize")}
        </button>
      </div>
    </>
  );
};

export default ConsentCard;
